// Medical Scores Database - Complete Collection Part 10
// Final batch of specialized scores

import {
  Brain,
  Heart,
  Wind,
  Activity,
  Baby,
  Stethoscope,
  Thermometer,
  Droplets,
  Flame,
  Syringe,
  Pill,
  Bone,
  Scale,
  Clock,
  AlertTriangle,
  Calculator,
  Beaker,
  User,
  Target,
  Shield,
  Zap,
  Eye
} from 'lucide-react';

export const SCORES_PART_10 = {
  // Dubowitz - Maturité nouveau-né
  dubowitz: {
    name: 'Score de Dubowitz (simplifié)',
    category: 'Pédiatrie',
    icon: Baby,
    description: 'Estimation âge gestationnel nouveau-né',
    color: 'violet',
    fields: [
      { id: 'edema', label: 'Œdème', type: 'select', options: [
        { value: 0, label: 'Œdème évident (0 pt)' },
        { value: 1, label: 'Œdème léger (1 pt)' },
        { value: 2, label: 'Pas d\'œdème (2 pts)' }
      ]},
      { id: 'skinTexture', label: 'Texture peau', type: 'select', options: [
        { value: 0, label: 'Très fine, gélatineuse (0 pt)' },
        { value: 1, label: 'Fine et lisse (1 pt)' },
        { value: 2, label: 'Lisse, épaisseur moyenne (2 pts)' },
        { value: 3, label: 'Légère desquamation (3 pts)' },
        { value: 4, label: 'Craquements, desquamation (4 pts)' }
      ]},
      { id: 'skinColor', label: 'Couleur peau', type: 'select', options: [
        { value: 0, label: 'Rouge foncé (0 pt)' },
        { value: 1, label: 'Rose uniforme (1 pt)' },
        { value: 2, label: 'Pâle, variable (2 pts)' },
        { value: 3, label: 'Pâle, rose extrémités seules (3 pts)' }
      ]},
      { id: 'skinOpacity', label: 'Transparence peau', type: 'select', options: [
        { value: 0, label: 'Veines visibles abdomen (0 pt)' },
        { value: 1, label: 'Veines peu visibles (1 pt)' },
        { value: 2, label: 'Quelques gros vaisseaux (2 pts)' },
        { value: 3, label: 'Pas de vaisseau visible (3 pts)' }
      ]},
      { id: 'lanugo', label: 'Lanugo', type: 'select', options: [
        { value: 0, label: 'Absent (0 pt)' },
        { value: 1, label: 'Abondant (1 pt)' },
        { value: 2, label: 'Fin et clairsemé (2 pts)' },
        { value: 3, label: 'Zones glabres (3 pts)' },
        { value: 4, label: 'Absent partout (4 pts)' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      // Estimation simplifiée AG
      const ag = 24 + (total * 0.5);
      return { score: total, max: 16, ag: ag.toFixed(0) };
    },
    interpret: (score, calculated) => {
      const ag = calculated?.ag || 40;
      if (ag < 28) return { level: 'danger', text: `AG ~${ag} SA`, detail: 'Grande prématurité' };
      if (ag < 32) return { level: 'danger', text: `AG ~${ag} SA`, detail: 'Prématurité sévère' };
      if (ag < 37) return { level: 'warning', text: `AG ~${ag} SA`, detail: 'Prématurité' };
      return { level: 'success', text: `AG ~${ag} SA`, detail: 'Terme' };
    },
    formula: 'Dubowitz: estimation AG par examen physique'
  },

  // EPWORTH - Somnolence
  epworth: {
    name: 'Échelle d\'Epworth',
    category: 'Pneumologie',
    icon: Wind,
    description: 'Évaluation somnolence diurne',
    color: 'sky',
    fields: [
      { id: 'sitting', label: 'Assis en train de lire', type: 'select', options: [
        { value: 0, label: '0 - Jamais' }, { value: 1, label: '1 - Faible' },
        { value: 2, label: '2 - Modéré' }, { value: 3, label: '3 - Fort' }
      ]},
      { id: 'tv', label: 'Regarder la télévision', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'inactive', label: 'Assis inactif en public', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'passenger', label: 'Passager voiture (1h sans arrêt)', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'afternoon', label: 'Allongé l\'après-midi', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'talking', label: 'Assis en parlant à quelqu\'un', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'lunch', label: 'Assis après déjeuner sans alcool', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'traffic', label: 'En voiture, arrêté dans le trafic', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 24 };
    },
    interpret: (score) => {
      if (score <= 6) return { level: 'success', text: `Epworth ${score}/24`, detail: 'Somnolence normale' };
      if (score <= 10) return { level: 'warning', text: `Epworth ${score}/24`, detail: 'Somnolence légère - Surveillance' };
      if (score <= 15) return { level: 'warning', text: `Epworth ${score}/24`, detail: 'Somnolence modérée - Polygraphie recommandée' };
      return { level: 'danger', text: `Epworth ${score}/24`, detail: 'Somnolence sévère - Danger conduite, bilan SAOS' };
    },
    formula: 'Epworth: 8 situations (0-3). >10 = somnolence excessive'
  },

  // Ferritin Saturation
  ironSaturation: {
    name: 'Coefficient de saturation Tf',
    category: 'Hématologie',
    icon: Droplets,
    description: 'Saturation de la transferrine',
    color: 'red',
    fields: [
      { id: 'iron', label: 'Fer sérique (µmol/L)', type: 'number', min: 1, max: 100, step: 0.1 },
      { id: 'tibc', label: 'TIBC ou CTF (µmol/L)', type: 'number', min: 20, max: 150 }
    ],
    calculate: (values) => {
      const iron = values.iron || 15;
      const tibc = values.tibc || 60;
      const sat = (iron / tibc) * 100;
      return { score: sat.toFixed(0), max: 100, unit: '%' };
    },
    interpret: (score) => {
      const s = parseInt(score);
      if (s < 16) return { level: 'danger', text: `CST ${score}%`, detail: 'Carence martiale' };
      if (s <= 45) return { level: 'success', text: `CST ${score}%`, detail: 'Normal' };
      if (s <= 60) return { level: 'warning', text: `CST ${score}%`, detail: 'Surcharge en fer possible' };
      return { level: 'danger', text: `CST ${score}%`, detail: 'Surcharge en fer - Recherche hémochromatose' };
    },
    formula: 'CST = (Fer / TIBC) × 100. Normal 20-45%'
  },

  // FIB-4 - Fibrose hépatique
  fib4: {
    name: 'Score FIB-4',
    category: 'Hépato-Gastro',
    icon: Beaker,
    description: 'Fibrose hépatique non invasif',
    color: 'yellow',
    fields: [
      { id: 'age', label: 'Âge (ans)', type: 'number', min: 18, max: 100 },
      { id: 'ast', label: 'ASAT (UI/L)', type: 'number', min: 5, max: 1000 },
      { id: 'alt', label: 'ALAT (UI/L)', type: 'number', min: 5, max: 1000 },
      { id: 'platelets', label: 'Plaquettes (G/L)', type: 'number', min: 20, max: 600 }
    ],
    calculate: (values) => {
      const age = values.age || 50;
      const ast = values.ast || 30;
      const alt = values.alt || 30;
      const plt = values.platelets || 200;
      const fib4 = (age * ast) / (plt * Math.sqrt(alt));
      return { score: fib4.toFixed(2), max: '' };
    },
    interpret: (score) => {
      const f = parseFloat(score);
      if (f < 1.30) return { level: 'success', text: `FIB-4 ${score}`, detail: 'Fibrose avancée peu probable (VPN 90%)' };
      if (f < 2.67) return { level: 'warning', text: `FIB-4 ${score}`, detail: 'Zone indéterminée - Fibroscan recommandé' };
      return { level: 'danger', text: `FIB-4 ${score}`, detail: 'Fibrose avancée probable (VPP 80%)' };
    },
    formula: 'FIB-4 = (Âge × ASAT) / (PLT × √ALAT)'
  },

  // GFR CKD-EPI 2021
  ckdEpi2021: {
    name: 'CKD-EPI 2021',
    category: 'Néphrologie',
    icon: Droplets,
    description: 'Estimation DFG (formule 2021 sans ethnie)',
    color: 'blue',
    fields: [
      { id: 'creatinine', label: 'Créatinine (µmol/L)', type: 'number', min: 20, max: 2000 },
      { id: 'age', label: 'Âge (ans)', type: 'number', min: 18, max: 120 },
      { id: 'sex', label: 'Sexe', type: 'select', options: [
        { value: 'M', label: 'Homme' },
        { value: 'F', label: 'Femme' }
      ]}
    ],
    calculate: (values) => {
      const cr = (values.creatinine || 100) / 88.4; // mg/dL
      const age = values.age || 50;
      const isFemale = values.sex === 'F';
      
      const kappa = isFemale ? 0.7 : 0.9;
      const alpha = isFemale ? -0.241 : -0.302;
      const factor = isFemale ? 1.012 : 1;
      
      const gfr = 142 * Math.pow(Math.min(cr/kappa, 1), alpha) * 
                  Math.pow(Math.max(cr/kappa, 1), -1.200) * 
                  Math.pow(0.9938, age) * factor;
      
      return { score: gfr.toFixed(0), max: 150, unit: 'mL/min/1.73m²' };
    },
    interpret: (score) => {
      const gfr = parseInt(score);
      if (gfr >= 90) return { level: 'success', text: `DFG ${score}`, detail: 'Fonction rénale normale' };
      if (gfr >= 60) return { level: 'success', text: `DFG ${score}`, detail: 'IRC légère (G2)' };
      if (gfr >= 45) return { level: 'warning', text: `DFG ${score}`, detail: 'IRC modérée (G3a)' };
      if (gfr >= 30) return { level: 'warning', text: `DFG ${score}`, detail: 'IRC modérée-sévère (G3b)' };
      if (gfr >= 15) return { level: 'danger', text: `DFG ${score}`, detail: 'IRC sévère (G4)' };
      return { level: 'danger', text: `DFG ${score}`, detail: 'IRC terminale (G5)' };
    },
    formula: 'CKD-EPI 2021: sans facteur ethnique'
  },

  // NYHA - Insuffisance cardiaque
  nyha: {
    name: 'Classification NYHA',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Capacité fonctionnelle insuffisance cardiaque',
    color: 'rose',
    fields: [
      { id: 'class', label: 'Classe NYHA', type: 'select', options: [
        { value: 1, label: 'I - Pas de limitation activité physique' },
        { value: 2, label: 'II - Limitation légère (confortable au repos)' },
        { value: 3, label: 'III - Limitation marquée (dyspnée efforts légers)' },
        { value: 4, label: 'IV - Symptômes au repos' }
      ]}
    ],
    calculate: (values) => {
      return { score: values.class || 1, max: 4 };
    },
    interpret: (score) => {
      if (score === 1) return { level: 'success', text: 'NYHA I', detail: 'IC asymptomatique' };
      if (score === 2) return { level: 'warning', text: 'NYHA II', detail: 'IC légère - Traitement médical' };
      if (score === 3) return { level: 'danger', text: 'NYHA III', detail: 'IC modérée - Optimisation traitement' };
      return { level: 'danger', text: 'NYHA IV', detail: 'IC sévère - Assistance/Greffe à discuter' };
    },
    formula: 'NYHA I-IV: classification fonctionnelle IC'
  },

  // PASI - Psoriasis
  pasi: {
    name: 'Score PASI',
    category: 'Dermatologie',
    icon: User,
    description: 'Sévérité psoriasis',
    color: 'pink',
    fields: [
      { id: 'headArea', label: 'Tête - Surface (%)', type: 'select', options: [
        { value: 0, label: '0%' }, { value: 1, label: '1-9%' }, { value: 2, label: '10-29%' },
        { value: 3, label: '30-49%' }, { value: 4, label: '50-69%' }, { value: 5, label: '70-89%' }, { value: 6, label: '90-100%' }
      ]},
      { id: 'headErythema', label: 'Tête - Érythème (0-4)', type: 'number', min: 0, max: 4 },
      { id: 'headInduration', label: 'Tête - Induration (0-4)', type: 'number', min: 0, max: 4 },
      { id: 'headDesquamation', label: 'Tête - Desquamation (0-4)', type: 'number', min: 0, max: 4 },
      { id: 'trunkArea', label: 'Tronc - Surface', type: 'select', options: [
        { value: 0, label: '0%' }, { value: 1, label: '1-9%' }, { value: 2, label: '10-29%' },
        { value: 3, label: '30-49%' }, { value: 4, label: '50-69%' }, { value: 5, label: '70-89%' }, { value: 6, label: '90-100%' }
      ]},
      { id: 'trunkErythema', label: 'Tronc - Érythème', type: 'number', min: 0, max: 4 },
      { id: 'trunkInduration', label: 'Tronc - Induration', type: 'number', min: 0, max: 4 },
      { id: 'trunkDesquamation', label: 'Tronc - Desquamation', type: 'number', min: 0, max: 4 },
      { id: 'upperArea', label: 'Membres sup - Surface', type: 'select', options: [
        { value: 0, label: '0%' }, { value: 1, label: '1-9%' }, { value: 2, label: '10-29%' },
        { value: 3, label: '30-49%' }, { value: 4, label: '50-69%' }, { value: 5, label: '70-89%' }, { value: 6, label: '90-100%' }
      ]},
      { id: 'upperErythema', label: 'Membres sup - Érythème', type: 'number', min: 0, max: 4 },
      { id: 'upperInduration', label: 'Membres sup - Induration', type: 'number', min: 0, max: 4 },
      { id: 'upperDesquamation', label: 'Membres sup - Desquamation', type: 'number', min: 0, max: 4 },
      { id: 'lowerArea', label: 'Membres inf - Surface', type: 'select', options: [
        { value: 0, label: '0%' }, { value: 1, label: '1-9%' }, { value: 2, label: '10-29%' },
        { value: 3, label: '30-49%' }, { value: 4, label: '50-69%' }, { value: 5, label: '70-89%' }, { value: 6, label: '90-100%' }
      ]},
      { id: 'lowerErythema', label: 'Membres inf - Érythème', type: 'number', min: 0, max: 4 },
      { id: 'lowerInduration', label: 'Membres inf - Induration', type: 'number', min: 0, max: 4 },
      { id: 'lowerDesquamation', label: 'Membres inf - Desquamation', type: 'number', min: 0, max: 4 }
    ],
    calculate: (values) => {
      const head = 0.1 * (values.headArea || 0) * ((values.headErythema || 0) + (values.headInduration || 0) + (values.headDesquamation || 0));
      const trunk = 0.3 * (values.trunkArea || 0) * ((values.trunkErythema || 0) + (values.trunkInduration || 0) + (values.trunkDesquamation || 0));
      const upper = 0.2 * (values.upperArea || 0) * ((values.upperErythema || 0) + (values.upperInduration || 0) + (values.upperDesquamation || 0));
      const lower = 0.4 * (values.lowerArea || 0) * ((values.lowerErythema || 0) + (values.lowerInduration || 0) + (values.lowerDesquamation || 0));
      const total = head + trunk + upper + lower;
      return { score: total.toFixed(1), max: 72 };
    },
    interpret: (score) => {
      const p = parseFloat(score);
      if (p < 3) return { level: 'success', text: `PASI ${score}`, detail: 'Psoriasis léger' };
      if (p < 10) return { level: 'warning', text: `PASI ${score}`, detail: 'Psoriasis modéré' };
      return { level: 'danger', text: `PASI ${score}`, detail: 'Psoriasis sévère - Biothérapie à discuter' };
    },
    formula: 'PASI = 0.1×Tête + 0.3×Tronc + 0.2×MS + 0.4×MI'
  },

  // Pittsburgh Sleep Quality Index (simplifié)
  psqi: {
    name: 'PSQI (simplifié)',
    category: 'Psychiatrie',
    icon: Brain,
    description: 'Qualité du sommeil',
    color: 'indigo',
    fields: [
      { id: 'quality', label: 'Qualité subjective sommeil', type: 'select', options: [
        { value: 0, label: '0 - Très bonne' }, { value: 1, label: '1 - Assez bonne' },
        { value: 2, label: '2 - Assez mauvaise' }, { value: 3, label: '3 - Très mauvaise' }
      ]},
      { id: 'latency', label: 'Latence endormissement', type: 'select', options: [
        { value: 0, label: '0 - < 15 min' }, { value: 1, label: '1 - 16-30 min' },
        { value: 2, label: '2 - 31-60 min' }, { value: 3, label: '3 - > 60 min' }
      ]},
      { id: 'duration', label: 'Durée sommeil', type: 'select', options: [
        { value: 0, label: '0 - > 7h' }, { value: 1, label: '1 - 6-7h' },
        { value: 2, label: '2 - 5-6h' }, { value: 3, label: '3 - < 5h' }
      ]},
      { id: 'efficiency', label: 'Efficience sommeil (temps lit vs sommeil)', type: 'select', options: [
        { value: 0, label: '0 - > 85%' }, { value: 1, label: '1 - 75-84%' },
        { value: 2, label: '2 - 65-74%' }, { value: 3, label: '3 - < 65%' }
      ]},
      { id: 'disturbances', label: 'Perturbations sommeil', type: 'select', options: [
        { value: 0, label: '0 - Aucune' }, { value: 1, label: '1 - <1/sem' },
        { value: 2, label: '2 - 1-2/sem' }, { value: 3, label: '3 - ≥3/sem' }
      ]},
      { id: 'medication', label: 'Utilisation médicaments sommeil', type: 'select', options: [
        { value: 0, label: '0 - Jamais' }, { value: 1, label: '1 - <1/sem' },
        { value: 2, label: '2 - 1-2/sem' }, { value: 3, label: '3 - ≥3/sem' }
      ]},
      { id: 'dysfunction', label: 'Dysfonctionnement diurne', type: 'select', options: [
        { value: 0, label: '0 - Aucun' }, { value: 1, label: '1 - Léger' },
        { value: 2, label: '2 - Modéré' }, { value: 3, label: '3 - Sévère' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 21 };
    },
    interpret: (score) => {
      if (score <= 5) return { level: 'success', text: `PSQI ${score}`, detail: 'Bonne qualité de sommeil' };
      if (score <= 10) return { level: 'warning', text: `PSQI ${score}`, detail: 'Troubles légers du sommeil' };
      return { level: 'danger', text: `PSQI ${score}`, detail: 'Troubles significatifs du sommeil - Bilan' };
    },
    formula: 'PSQI > 5 = mauvaise qualité de sommeil'
  },

  // Ranson pancreatite
  ranson: {
    name: 'Score de Ranson',
    category: 'Hépato-Gastro',
    icon: Beaker,
    description: 'Pronostic pancréatite aiguë',
    color: 'yellow',
    fields: [
      { id: 'age', label: 'À l\'admission: Âge > 55 ans', type: 'checkbox', points: 1 },
      { id: 'wbc', label: 'À l\'admission: GB > 16 000', type: 'checkbox', points: 1 },
      { id: 'glucose', label: 'À l\'admission: Glycémie > 11 mmol/L', type: 'checkbox', points: 1 },
      { id: 'ldh', label: 'À l\'admission: LDH > 350 UI/L', type: 'checkbox', points: 1 },
      { id: 'ast', label: 'À l\'admission: ASAT > 250 UI/L', type: 'checkbox', points: 1 },
      { id: 'hct', label: 'À 48h: Chute Hte > 10%', type: 'checkbox', points: 1 },
      { id: 'bun', label: 'À 48h: Élévation urée > 1.8 mmol/L', type: 'checkbox', points: 1 },
      { id: 'calcium', label: 'À 48h: Calcium < 2 mmol/L', type: 'checkbox', points: 1 },
      { id: 'pao2', label: 'À 48h: PaO₂ < 60 mmHg', type: 'checkbox', points: 1 },
      { id: 'baseDeficit', label: 'À 48h: Déficit base > 4 mEq/L', type: 'checkbox', points: 1 },
      { id: 'fluid', label: 'À 48h: Séquestration > 6L', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += 1; });
      return { score: total, max: 11 };
    },
    interpret: (score) => {
      if (score < 3) return { level: 'success', text: `Ranson ${score}`, detail: 'Pancréatite légère - Mortalité ~1%' };
      if (score <= 5) return { level: 'warning', text: `Ranson ${score}`, detail: 'Pancréatite modérée - Mortalité ~15%' };
      return { level: 'danger', text: `Ranson ${score}`, detail: 'Pancréatite sévère - Mortalité ~40% - USI' };
    },
    formula: 'Ranson: 5 critères à J0 + 6 à 48h'
  },

  // SAPS II
  sapsII: {
    name: 'Score SAPS II (simplifié)',
    category: 'Réanimation',
    icon: Activity,
    description: 'Mortalité prédite en réanimation',
    color: 'red',
    fields: [
      { id: 'age', label: 'Âge', type: 'select', options: [
        { value: 0, label: '< 40 ans (0 pt)' },
        { value: 7, label: '40-59 ans (7 pts)' },
        { value: 12, label: '60-69 ans (12 pts)' },
        { value: 15, label: '70-74 ans (15 pts)' },
        { value: 16, label: '75-79 ans (16 pts)' },
        { value: 18, label: '≥ 80 ans (18 pts)' }
      ]},
      { id: 'hr', label: 'Fréquence cardiaque', type: 'select', options: [
        { value: 11, label: '< 40 (11 pts)' },
        { value: 2, label: '40-69 (2 pts)' },
        { value: 0, label: '70-119 (0 pt)' },
        { value: 4, label: '120-159 (4 pts)' },
        { value: 7, label: '≥ 160 (7 pts)' }
      ]},
      { id: 'sbp', label: 'PAS', type: 'select', options: [
        { value: 13, label: '< 70 (13 pts)' },
        { value: 5, label: '70-99 (5 pts)' },
        { value: 0, label: '100-199 (0 pt)' },
        { value: 2, label: '≥ 200 (2 pts)' }
      ]},
      { id: 'temp', label: 'Température', type: 'select', options: [
        { value: 0, label: '< 39°C (0 pt)' },
        { value: 3, label: '≥ 39°C (3 pts)' }
      ]},
      { id: 'pao2fio2', label: 'PaO₂/FiO₂ (si VM ou CPAP)', type: 'select', options: [
        { value: 0, label: 'Pas de VM (0 pt)' },
        { value: 6, label: '≥ 200 (6 pts)' },
        { value: 9, label: '100-199 (9 pts)' },
        { value: 11, label: '< 100 (11 pts)' }
      ]},
      { id: 'gcs', label: 'Glasgow', type: 'select', options: [
        { value: 0, label: '14-15 (0 pt)' },
        { value: 5, label: '11-13 (5 pts)' },
        { value: 7, label: '9-10 (7 pts)' },
        { value: 13, label: '6-8 (13 pts)' },
        { value: 26, label: '< 6 (26 pts)' }
      ]},
      { id: 'admission', label: 'Type admission', type: 'select', options: [
        { value: 0, label: 'Chirurgie programmée (0 pt)' },
        { value: 6, label: 'Médical (6 pts)' },
        { value: 8, label: 'Chirurgie non programmée (8 pts)' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      // Mortalité estimée (logistic)
      const logit = -7.7631 + 0.0737 * total + 0.9971 * Math.log(total + 1);
      const mortality = (Math.exp(logit) / (1 + Math.exp(logit))) * 100;
      return { score: total, max: 163, mortality: mortality.toFixed(1) };
    },
    interpret: (score, calculated) => {
      const m = calculated?.mortality || 0;
      if (score < 30) return { level: 'success', text: `SAPS II ${score}`, detail: `Mortalité prédite: ~${m}%` };
      if (score < 50) return { level: 'warning', text: `SAPS II ${score}`, detail: `Mortalité prédite: ~${m}%` };
      if (score < 70) return { level: 'danger', text: `SAPS II ${score}`, detail: `Mortalité prédite: ~${m}%` };
      return { level: 'danger', text: `SAPS II ${score}`, detail: `Mortalité prédite: ~${m}% - Pronostic très réservé` };
    },
    formula: 'SAPS II: version simplifiée. Score complet avec biologie'
  },

  // Schwab & England - Parkinson
  schwabEngland: {
    name: 'Échelle de Schwab & England',
    category: 'Neurologie',
    icon: Brain,
    description: 'Autonomie maladie de Parkinson',
    color: 'purple',
    fields: [
      { id: 'score', label: 'Autonomie', type: 'select', options: [
        { value: 100, label: '100% - Totalement indépendant' },
        { value: 90, label: '90% - Indépendant, légère lenteur' },
        { value: 80, label: '80% - Indépendant, tâches 2x plus longues' },
        { value: 70, label: '70% - Pas totalement indépendant, difficultés' },
        { value: 60, label: '60% - Partiellement dépendant' },
        { value: 50, label: '50% - Plus dépendant, aide 50% tâches' },
        { value: 40, label: '40% - Très dépendant' },
        { value: 30, label: '30% - Avec effort, quelques tâches seul' },
        { value: 20, label: '20% - Rien seul, aide constante' },
        { value: 10, label: '10% - Totalement dépendant, grabataire' },
        { value: 0, label: '0% - Fonctions végétatives seules' }
      ]}
    ],
    calculate: (values) => {
      return { score: values.score || 100, max: 100 };
    },
    interpret: (score) => {
      if (score >= 80) return { level: 'success', text: `S&E ${score}%`, detail: 'Indépendance conservée' };
      if (score >= 50) return { level: 'warning', text: `S&E ${score}%`, detail: 'Dépendance partielle' };
      return { level: 'danger', text: `S&E ${score}%`, detail: 'Dépendance majeure' };
    },
    formula: 'Schwab & England: 0-100% capacité fonctionnelle'
  }
};
